/**
 * How tall the GHL frame actually is on screen.
 *
 * `100vh` inside a frame is the *frame element's* height, which the host page
 * sets and this app cannot read. When GHL renders the frame taller than the room
 * left under its own chrome — its header, plus whatever promo banner is up that
 * week — the bottom band of the frame is below the browser window's fold, and
 * nothing can reach it: the outer page does not scroll down to it, and inside
 * the frame the document fits 100vh exactly, so there is no overflow to scroll
 * either. The end of every page, and the sidebar's sign-out row, sit in that
 * band. Sizing the shell to 100vh is what puts them there.
 *
 * So measure instead of trusting. An IntersectionObserver with no `root` is
 * computed against the *top-level* viewport and clipped by every ancestor frame
 * on the way down, and it does that across origins — which is the only reason
 * this is possible at all, since app.apexpro.ai and harbor.apexpro.ai are
 * same-site but not same-origin, so the parent's DOM is off limits. Observing a
 * full-height sentinel therefore reports exactly how much of the frame the
 * reader can see, and `--frame-h` carries it to the stylesheet.
 */

/** Below this, assume the measurement is junk rather than a very short frame. */
const MIN_PLAUSIBLE_PX = 320;

/**
 * A ratio alone never fires again once it stops crossing a threshold, and the
 * interesting changes here are small: a banner appearing shifts the frame by a
 * few percent. A fine ladder keeps the callback live across that whole range.
 */
const THRESHOLDS = Array.from({ length: 101 }, (_, i) => i / 100);

/**
 * Starts measuring, and keeps measuring. Returns a teardown for tests; nothing
 * in the app calls it, because the observer should outlive every route.
 */
export function trackFrameHeight(): () => void {
  // Full frame height, out of flow and untouchable, so it measures the frame
  // rather than joining the layout it is measuring.
  const probe = document.createElement('div');
  probe.setAttribute('aria-hidden', 'true');
  probe.style.cssText =
    'position:fixed;top:0;left:0;width:1px;height:100vh;pointer-events:none;visibility:hidden;z-index:-1';
  document.body.appendChild(probe);

  const apply = (visiblePx: number): void => {
    // A frame scrolled out of view, or a backgrounded tab, measures 0 or near
    // it. Keeping the last good value beats collapsing the shell to nothing and
    // then having to rebuild it when the reader comes back.
    if (visiblePx < MIN_PLAUSIBLE_PX) return;
    // The visible part cannot exceed the frame, and rounding up would
    // reintroduce the overflow this exists to remove.
    const h = Math.min(Math.floor(visiblePx), probe.offsetHeight);
    document.documentElement.style.setProperty('--frame-h', `${h}px`);
  };

  const io = new IntersectionObserver(
    (entries) => apply(entries[entries.length - 1].intersectionRect.height),
    { threshold: THRESHOLDS },
  );
  io.observe(probe);

  // The observer covers host scrolling and banners. This covers the case where
  // the frame's own height changes with the window and the visible *fraction*
  // stays put, which crosses no threshold and so fires nothing.
  const onResize = (): void => apply(probe.getBoundingClientRect().height);
  window.addEventListener('resize', onResize);

  return () => {
    io.disconnect();
    window.removeEventListener('resize', onResize);
    probe.remove();
    document.documentElement.style.removeProperty('--frame-h');
  };
}
